import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { getDropIdentityFallbackValue, getDropIdentityProfile } from "@/components/waves/drops/identityDisplay.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale, getScaledImageUri } from "@/helpers/image.helpers";
import { WavePodiumItemContentOutcomes } from "./WavePodiumItemContentOutcomes";
import type { ApiWaveDecisionWinner } from "@/generated/models/ApiWaveDecisionWinner";
import { motion } from "framer-motion";
import { WaveWinnersPodiumPlaceholder } from "./WaveWinnersPodiumPlaceholder";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import ParticipationDropVoteDetailsTrigger from "@/components/waves/drops/participation/ratings/ParticipationDropVoteDetailsTrigger";
interface WavePodiumItemProps {
  readonly winner?: ApiWaveDecisionWinner | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly position: "first" | "second" | "third";
  readonly customAnimationIndex?: number | undefined;
  readonly showVoteDetails?: boolean | undefined;
}
interface PodiumAvatarProps {
  readonly label: string;
  readonly pfp: string | null | undefined;
  readonly alt: string;
  readonly width: number;
  readonly height: number;
  readonly className: string;
  readonly ringClass: string;
  readonly ringWidthClass?: string | undefined;
  readonly shadowClass?: string | undefined;
  readonly fallbackTextClass?: string | undefined;
}

// Configuration for position-specific styling with CSS ready classes for Tailwind
const positionStyles = {
  first: {
    color: "#fbbf24",
    // amber-400 - Gold from WinnerDropBadge
    height: "tw-h-[220px]",
    pfpSize: "tw-size-12 md:tw-size-14",
    marginBottom: "-tw-mb-6",
    textColor: "tw-text-[#fbbf24]",
    ring: "tw-ring-[#fbbf24]",
    shadow: "tw-shadow-[0_0_20px_rgba(251,191,36,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base md:tw-text-xl",
    ratingFontSize: "tw-text-sm sm:tw-text-base md:tw-text-2xl",
    positionText: "1st",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#fbbf24]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#fbbf24]/5",
      via: "tw-via-[#fbbf24]/3",
      hover: "group-hover:desktop-hover:tw-from-[#fbbf24]/[0.07]",
      top: "tw-via-[#fbbf24]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#fbbf24]/25",
      sides: "tw-from-[#fbbf24]/10 tw-via-[#fbbf24]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#fbbf24]/25"
    }
  },
  second: {
    color: "#94a3b8",
    // slate-400 - Silver from WinnerDropBadge
    height: "tw-h-[190px]",
    pfpSize: "tw-size-9 md:tw-size-11",
    marginBottom: "-tw-mb-2",
    textColor: "tw-text-[#94a3b8]",
    ring: "tw-ring-[#94a3b8]",
    shadow: "tw-shadow-[0_0_20px_rgba(148,163,184,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base",
    ratingFontSize: "tw-text-sm sm:tw-text-base",
    positionText: "2nd",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#94a3b8]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#94a3b8]/5",
      via: "tw-via-[#94a3b8]/3",
      hover: "group-hover:desktop-hover:tw-from-[#94a3b8]/[0.07]",
      top: "tw-via-[#94a3b8]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#94a3b8]/25",
      sides: "tw-from-[#94a3b8]/10 tw-via-[#94a3b8]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#94a3b8]/25"
    }
  },
  third: {
    color: "#CD7F32",
    // Bronze (same in both components)
    height: "tw-h-[170px]",
    pfpSize: "tw-size-9 md:tw-size-11",
    marginBottom: "-tw-mb-2",
    textColor: "tw-text-[#CD7F32]",
    ring: "tw-ring-[#CD7F32]",
    shadow: "tw-shadow-[0_0_20px_rgba(205,127,50,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base",
    ratingFontSize: "tw-text-base",
    positionText: "3rd",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#CD7F32]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#CD7F32]/5",
      via: "tw-via-[#CD7F32]/3",
      hover: "group-hover:desktop-hover:tw-from-[#CD7F32]/[0.07]",
      top: "tw-via-[#CD7F32]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#CD7F32]/25",
      sides: "tw-from-[#CD7F32]/10 tw-via-[#CD7F32]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#CD7F32]/25"
    }
  }
};

// Animation variants for the podium items
const podiumVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.98
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: [0.2, 0.9, 0.3, 1],
      opacity: {
        duration: 0.25
      }
    }
  })
};

// Height mapping for different positions
const heightMap = {
  first: "tw-h-[220px]",
  second: "tw-h-[190px]",
  third: "tw-h-[170px]"
};
const animationIndexMap: Record<WavePodiumItemProps["position"], number> = {
  first: 0,
  second: 1,
  third: 2
};
const getHoverTextColorClass = (position: WavePodiumItemProps["position"]) => {
  const colorMap = {
    first: "desktop-hover:hover:tw-text-[#fbbf24]",
    second: "desktop-hover:hover:tw-text-[#94a3b8]",
    third: "desktop-hover:hover:tw-text-[#CD7F32]"
  } as const;
  return colorMap[position];
};
const getAuthorProfileLabel = (drop: ExtendedDrop): string => drop.author.handle ?? drop.author.primary_address;
const getAuthorTooltipUser = (drop: ExtendedDrop): string => drop.author.handle ?? drop.author.primary_address;
const getIdentityHref = (value: string) => `/${encodeURIComponent(value.toLowerCase())}`;
const getPodiumIdentityDisplay = (drop: ExtendedDrop) => {
  const identityProfile = getDropIdentityProfile({
    wave: drop.wave,
    metadata: drop.metadata
  });
  const fallbackValue = identityProfile ? null : getDropIdentityFallbackValue({
    wave: drop.wave,
    metadata: drop.metadata
  });
  const label = identityProfile?.handle ?? identityProfile?.primary_address ?? fallbackValue;
  if (!label) {
    return null;
  }
  return {
    label,
    pfp: identityProfile?.pfp ?? null,
    profileUser: identityProfile?.handle ?? identityProfile?.primary_address ?? null,
    comparableIdentity: identityProfile ?? {
      handle: fallbackValue,
      primary_address: fallbackValue
    }
  };
};
const PodiumAvatar: React.FC<PodiumAvatarProps> = t0 => {
  const $ = _c(16);
  const {
    label,
    pfp,
    alt,
    width,
    height,
    className,
    ringClass,
    ringWidthClass: t1,
    shadowClass,
    fallbackTextClass: t2
  } = t0;
  const ringWidthClass = t1 === undefined ? "tw-ring-2" : t1;
  const fallbackTextClass = t2 === undefined ? "tw-text-xs tw-font-semibold tw-text-iron-100" : t2;
  let t3;
  if ($[0] !== label) {
    t3 = label.trim().charAt(0).toUpperCase() || "?";
    $[0] = label;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const initial = t3;
  if (pfp) {
    let t4;
    if ($[2] !== pfp) {
      t4 = getScaledImageUri(pfp, ImageScale.W_AUTO_H_50);
      $[2] = pfp;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    const t5 = `${className} ${ringWidthClass} ${ringClass} tw-object-cover ${shadowClass ?? ""}`;
    let t6;
    if ($[4] !== alt || $[5] !== height || $[6] !== t4 || $[7] !== t5 || $[8] !== width) {
      t6 = <Image src={t4} alt={alt} width={width} height={height} className={t5} />;
      $[4] = alt;
      $[5] = height;
      $[6] = t4;
      $[7] = t5;
      $[8] = width;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  const t4 = `${className} ${ringWidthClass} ${ringClass} ${shadowClass ?? ""} tw-flex tw-items-center tw-justify-center tw-bg-iron-900`;
  let t5;
  if ($[10] !== fallbackTextClass || $[11] !== initial) {
    t5 = <span className={fallbackTextClass}>{initial}</span>;
    $[10] = fallbackTextClass;
    $[11] = initial;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div aria-hidden="true" className={t4}>{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
export const WavePodiumItem: React.FC<WavePodiumItemProps> = t0 => {
  const $ = _c(122);
  const {
    winner,
    onDropClick,
    position,
    customAnimationIndex,
    showVoteDetails: t1
  } = t0;
  const showVoteDetails = t1 === undefined ? true : t1;
  const styles = positionStyles[position];
  let t2;
  if ($[0] !== position) {
    t2 = getHoverTextColorClass(position);
    $[0] = position;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const hoverTextColorClass = t2;
  if (!winner) {
    const t3 = heightMap[position];
    let t4;
    if ($[2] !== position || $[3] !== t3) {
      t4 = <WaveWinnersPodiumPlaceholder height={t3} position={position} />;
      $[2] = position;
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  const drop = winner.drop as ExtendedDrop;
  const animationIndex = customAnimationIndex ?? animationIndexMap[position];
  let t3;
  if ($[5] !== drop) {
    t3 = getAuthorProfileLabel(drop);
    $[5] = drop;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const authorProfileLabel = t3;
  let t4;
  if ($[7] !== drop) {
    t4 = getAuthorTooltipUser(drop);
    $[7] = drop;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const authorTooltipUser = t4;
  const authorProfileHref = `/${authorProfileLabel}`;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== animationIndex || $[10] !== authorProfileHref || $[11] !== authorProfileLabel || $[12] !== authorTooltipUser || $[13] !== drop || $[14] !== hoverTextColorClass || $[15] !== onDropClick || $[16] !== styles.autorFontSize || $[17] !== styles.bgGradient || $[18] !== styles.gradient.from || $[19] !== styles.gradient.hover || $[20] !== styles.gradient.hover25 || $[21] !== styles.gradient.sides || $[22] !== styles.gradient.sidesHover || $[23] !== styles.gradient.top || $[24] !== styles.gradient.via || $[25] !== styles.height || $[26] !== styles.marginBottom || $[27] !== styles.pfpSize || $[28] !== styles.positionText || $[29] !== styles.ring || $[30] !== styles.shadow || $[31] !== styles.textColor) {
    const identityDisplay = getPodiumIdentityDisplay(drop);
    const primaryLabel = identityDisplay?.label ?? authorProfileLabel;
    const primaryPfp = identityDisplay ? identityDisplay.pfp : drop.author.pfp;
    const isSelfNominated = identityDisplay ? areSameProfileIdentity({
      left: drop.author,
      right: identityDisplay.comparableIdentity
    }) : false;
    T0 = motion.div;
    t13 = podiumVariants;
    t14 = "hidden";
    t15 = "visible";
    t16 = animationIndex;
    if ($[45] !== drop || $[46] !== onDropClick) {
      t11 = () => onDropClick(drop);
      $[45] = drop;
      $[46] = onDropClick;
      $[47] = t11;
    } else {
      t11 = $[47];
    }
    t12 = "tw-group tw-cursor-pointer";
    t9 = "tw-flex tw-flex-col tw-items-center";
    const t17 = `tw-flex tw-flex-col tw-items-center ${styles.marginBottom} tw-relative tw-z-10`;
    const t18 = `tw-absolute tw-inset-0 ${styles.bgGradient}`;
    let t19;
    if ($[48] !== t18) {
      t19 = <div className={t18} />;
      $[48] = t18;
      $[49] = t19;
    } else {
      t19 = $[49];
    }
    const t20 = identityDisplay ? <div className="tw-transform tw-transition-transform tw-duration-300 group-hover:desktop-hover:tw-scale-[1.02]"><PodiumAvatar label={primaryLabel} pfp={primaryPfp} alt={`${primaryLabel} avatar`} width={56} height={56} className={`${styles.pfpSize} tw-rounded-xl`} ringClass={styles.ring} shadowClass={styles.shadow} /></div> : <Link href={authorProfileHref} onClick={_temp} className="tw-transform tw-transition-all tw-duration-300 hover:tw-scale-105"><PodiumAvatar label={authorProfileLabel} pfp={drop.author.pfp} alt={`${authorProfileLabel} avatar`} width={56} height={56} className={`${styles.pfpSize} tw-rounded-xl`} ringClass={styles.ring} shadowClass={styles.shadow} /></Link>;
    const t21 = `tw-w-3 md:tw-size-4 ${styles.textColor}`;
    let t22;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <path fill="currentColor" d="M400 0L176 0c-26.5 0-48.1 21.8-47.1 48.2c.2 5.3 .4 10.6 .7 15.8L24 64C10.7 64 0 74.7 0 88c0 92.6 33.5 157 78.5 200.7c44.3 43.1 98.3 64.8 138.1 75.8c23.4 6.5 39.4 26 39.4 45.6c0 20.9-17 37.9-37.9 37.9L192 448c-17.7 0-32 14.3-32 32s14.3 32 32 32l192 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-26.1 0C337 448 320 431 320 410.1c0-19.6 15.9-39.2 39.4-45.6c39.9-11 93.9-32.7 138.2-75.8C542.5 245 576 180.6 576 88c0-13.3-10.7-24-24-24L446.4 64c.3-5.2 .5-10.4 .7-15.8C448.1 21.8 426.5 0 400 0zM48.9 112l84.4 0c9.1 90.1 29.2 150.3 51.9 190.6c-24.9-11-50.8-26.5-73.2-48.3c-32-31.1-58-76-63-142.3zM464.1 254.3c-22.4 21.8-48.3 37.3-73.2 48.3c22.7-40.3 42.8-100.5 51.9-190.6l84.4 0c-5.1 66.3-31.1 111.2-63 142.3z" />;
      $[50] = t22;
    } else {
      t22 = $[50];
    }
    let t23;
    if ($[51] !== t21) {
      t23 = <svg className={t21} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">{t22}</svg>;
      $[51] = t21;
      $[52] = t23;
    } else {
      t23 = $[52];
    }
    const t24 = `${styles.textColor} tw-text-xs tw-font-medium lg:tw-text-sm`;
    let t25;
    if ($[53] !== styles.positionText || $[54] !== t24) {
      t25 = <span className={t24}>{styles.positionText}</span>;
      $[53] = styles.positionText;
      $[54] = t24;
      $[55] = t25;
    } else {
      t25 = $[55];
    }
    let t26;
    if ($[56] !== t23 || $[57] !== t25) {
      t26 = <div className="tw-absolute tw-inset-x-0 -tw-bottom-3 tw-flex tw-justify-center"><div className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-iron-700 tw-bg-iron-900/80 tw-px-3 tw-py-1 tw-shadow-lg tw-backdrop-blur-sm">{t23}{t25}</div></div>;
      $[56] = t23;
      $[57] = t25;
      $[58] = t26;
    } else {
      t26 = $[58];
    }
    if ($[59] !== t17 || $[60] !== t19 || $[61] !== t20 || $[62] !== t26) {
      t10 = <div className={t17}>{t19}{t20}{t26}</div>;
      $[59] = t17;
      $[60] = t19;
      $[61] = t20;
      $[62] = t26;
      $[63] = t10;
    } else {
      t10 = $[63];
    }
    t8 = "tw-relative tw-w-full";
    t5 = `${styles.height} tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-border tw-border-iron-800/60 tw-bg-gradient-to-b tw-from-iron-900/70 tw-via-iron-900/50 tw-to-transparent tw-shadow-[0_4px_24px_rgba(0,0,0,0.2)] tw-backdrop-blur-xl tw-transition-all tw-duration-300 tw-ease-out group-hover:desktop-hover:tw-border-iron-700/70 group-hover:desktop-hover:tw-from-iron-900/75 group-hover:desktop-hover:tw-via-iron-900/55 group-hover:desktop-hover:tw-to-transparent group-hover:desktop-hover:tw-shadow-[0_0_48px_rgba(0,0,0,0.35)]`;
    const t27 = `tw-absolute tw-inset-0 tw-bg-gradient-to-b ${styles.gradient.from} ${styles.gradient.via} tw-to-transparent ${styles.gradient.hover}`;
    let t28;
    if ($[64] !== t27) {
      t28 = <div className={t27} />;
      $[64] = t27;
      $[65] = t28;
    } else {
      t28 = $[65];
    }
    const t29 = `tw-absolute tw-inset-x-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent ${styles.gradient.top} tw-to-transparent ${styles.gradient.hover25}`;
    let t30;
    if ($[66] !== t29) {
      t30 = <div className={t29} />;
      $[66] = t29;
      $[67] = t30;
    } else {
      t30 = $[67];
    }
    const t31 = `tw-absolute tw-inset-y-0 tw-right-0 tw-w-px tw-bg-gradient-to-b ${styles.gradient.sides} tw-to-transparent ${styles.gradient.sidesHover}`;
    let t32;
    if ($[68] !== t31) {
      t32 = <div className={t31} />;
      $[68] = t31;
      $[69] = t32;
    } else {
      t32 = $[69];
    }
    const t33 = `tw-absolute tw-inset-y-0 tw-left-0 tw-w-px tw-bg-gradient-to-b ${styles.gradient.sides} tw-to-transparent ${styles.gradient.sidesHover}`;
    let t34;
    if ($[70] !== t33) {
      t34 = <div className={t33} />;
      $[70] = t33;
      $[71] = t34;
    } else {
      t34 = $[71];
    }
    let t35;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-3/4 tw-bg-gradient-to-t tw-from-black/20 tw-via-black/10 tw-to-transparent" />;
      $[72] = t35;
    } else {
      t35 = $[72];
    }
    if ($[73] !== t28 || $[74] !== t30 || $[75] !== t32 || $[76] !== t34) {
      t6 = <div className="tw-absolute tw-inset-0">{t28}{t30}{t32}{t34}{t35}</div>;
      $[73] = t28;
      $[74] = t30;
      $[75] = t32;
      $[76] = t34;
      $[77] = t6;
    } else {
      t6 = $[77];
    }
    t7 = identityDisplay ? <div className="tw-mb-2 tw-mt-2 tw-flex tw-max-w-full tw-flex-col tw-items-center tw-gap-y-1 tw-px-3 sm:tw-mt-3">{identityDisplay.profileUser ? <UserProfileTooltipWrapper user={identityDisplay.profileUser}><Link href={getIdentityHref(identityDisplay.profileUser)} onClick={_temp2} className={`tw-relative tw-block tw-max-w-full tw-text-center tw-no-underline tw-transition-all ${hoverTextColorClass} tw-group/link`}><span title={primaryLabel} className={`${styles.autorFontSize} tw-block tw-max-w-full tw-truncate tw-font-semibold tw-text-iron-100 ${hoverTextColorClass} tw-transition-colors`}>{primaryLabel}</span><svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={`tw-size-3 tw-opacity-0 tw-transition-opacity ${styles.textColor} tw-absolute tw-left-[100%] tw-top-1/2 tw-ml-2 -tw-translate-y-1/2 desktop-hover:group-hover/link:tw-opacity-100`}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" /></svg></Link></UserProfileTooltipWrapper> : <span title={primaryLabel} className={`${styles.autorFontSize} tw-block tw-max-w-full tw-truncate tw-text-center tw-font-semibold tw-text-iron-100`}>{primaryLabel}</span>}<div className="tw-flex tw-max-w-full tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-1 tw-gap-y-1 tw-text-[11px] tw-text-iron-500">{isSelfNominated ? <span className="tw-font-normal tw-text-iron-500">self-nominated</span> : <><span className="tw-font-normal tw-text-iron-500">nominated by</span><UserProfileTooltipWrapper user={authorTooltipUser}><Link href={authorProfileHref} onClick={_temp3} className="tw-inline-flex tw-max-w-full tw-items-center tw-text-iron-400 tw-no-underline tw-transition-colors desktop-hover:hover:tw-text-iron-200"><span title={authorProfileLabel} className="tw-block tw-max-w-[7rem] tw-truncate tw-text-[11px] tw-font-medium tw-text-iron-400 tw-transition-colors desktop-hover:hover:tw-text-iron-200">{authorProfileLabel}</span></Link></UserProfileTooltipWrapper></>}</div></div> : <UserProfileTooltipWrapper user={authorTooltipUser}><Link href={authorProfileHref} onClick={_temp4} className={`tw-relative tw-mb-2 tw-mt-2 tw-text-center tw-no-underline tw-transition-all sm:tw-mt-4 ${hoverTextColorClass} tw-group/link`}><span className={`${styles.autorFontSize} tw-font-semibold tw-text-iron-200 ${hoverTextColorClass} tw-inline-flex tw-items-center tw-transition-colors`}>{authorProfileLabel}<svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={`tw-size-3 tw-opacity-0 tw-transition-opacity ${styles.textColor} tw-absolute tw-left-[100%] tw-ml-2 desktop-hover:group-hover/link:tw-opacity-100`}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" /></svg></span></Link></UserProfileTooltipWrapper>;
    $[9] = animationIndex;
    $[10] = authorProfileHref;
    $[11] = authorProfileLabel;
    $[12] = authorTooltipUser;
    $[13] = drop;
    $[14] = hoverTextColorClass;
    $[15] = onDropClick;
    $[16] = styles.autorFontSize;
    $[17] = styles.bgGradient;
    $[18] = styles.gradient.from;
    $[19] = styles.gradient.hover;
    $[20] = styles.gradient.hover25;
    $[21] = styles.gradient.sides;
    $[22] = styles.gradient.sidesHover;
    $[23] = styles.gradient.top;
    $[24] = styles.gradient.via;
    $[25] = styles.height;
    $[26] = styles.marginBottom;
    $[27] = styles.pfpSize;
    $[28] = styles.positionText;
    $[29] = styles.ring;
    $[30] = styles.shadow;
    $[31] = styles.textColor;
    $[32] = T0;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
  } else {
    T0 = $[32];
    t10 = $[33];
    t11 = $[34];
    t12 = $[35];
    t13 = $[36];
    t14 = $[37];
    t15 = $[38];
    t16 = $[39];
    t5 = $[40];
    t6 = $[41];
    t7 = $[42];
    t8 = $[43];
    t9 = $[44];
  }
  const t17 = `${drop.rating >= 0 ? styles.textColor : "tw-text-[#ff4466]"} tw-font-semibold ${styles.ratingFontSize}`;
  let t18;
  if ($[78] !== drop.rating) {
    t18 = formatNumberWithCommas(drop.rating);
    $[78] = drop.rating;
    $[79] = t18;
  } else {
    t18 = $[79];
  }
  let t19;
  if ($[80] !== t17 || $[81] !== t18) {
    t19 = <span className={t17}>{t18}</span>;
    $[80] = t17;
    $[81] = t18;
    $[82] = t19;
  } else {
    t19 = $[82];
  }
  const t20 = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  let t21;
  if ($[83] !== t20) {
    t21 = <span className="tw-text-xs tw-text-iron-400 sm:tw-text-sm">{t20}</span>;
    $[83] = t20;
    $[84] = t21;
  } else {
    t21 = $[84];
  }
  let t22;
  if ($[85] !== t19 || $[86] !== t21) {
    t22 = <div className="tw-flex tw-items-center tw-gap-x-1">{t19}{t21}</div>;
    $[85] = t19;
    $[86] = t21;
    $[87] = t22;
  } else {
    t22 = $[87];
  }
  let t23;
  if ($[88] !== drop || $[89] !== showVoteDetails) {
    t23 = showVoteDetails ? <ParticipationDropVoteDetailsTrigger drop={drop} density="compact" /> : <div className="tw-flex tw-items-center tw-gap-x-1"><span className="tw-text-xs tw-text-iron-200 sm:tw-text-sm">{formatNumberWithCommas(drop.raters_count)}</span><span className="tw-text-xs tw-text-iron-400 sm:tw-text-sm">{drop.raters_count === 1 ? "voter" : "voters"}</span></div>;
    $[88] = drop;
    $[89] = showVoteDetails;
    $[90] = t23;
  } else {
    t23 = $[90];
  }
  let t24;
  if ($[91] !== winner) {
    t24 = <div onClick={_temp5}><WavePodiumItemContentOutcomes winner={winner} /></div>;
    $[91] = winner;
    $[92] = t24;
  } else {
    t24 = $[92];
  }
  let t25;
  if ($[93] !== t23 || $[94] !== t24) {
    t25 = <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-2">{t23}{t24}</div>;
    $[93] = t23;
    $[94] = t24;
    $[95] = t25;
  } else {
    t25 = $[95];
  }
  let t26;
  if ($[96] !== t22 || $[97] !== t25) {
    t26 = <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-gap-y-2">{t22}{t25}</div>;
    $[96] = t22;
    $[97] = t25;
    $[98] = t26;
  } else {
    t26 = $[98];
  }
  let t27;
  if ($[99] !== t26 || $[100] !== t5 || $[101] !== t6 || $[102] !== t7) {
    t27 = <div className={t5}>{t6}{t7}{t26}</div>;
    $[99] = t26;
    $[100] = t5;
    $[101] = t6;
    $[102] = t7;
    $[103] = t27;
  } else {
    t27 = $[103];
  }
  let t28;
  if ($[104] !== t27 || $[105] !== t8) {
    t28 = <div className={t8}>{t27}</div>;
    $[104] = t27;
    $[105] = t8;
    $[106] = t28;
  } else {
    t28 = $[106];
  }
  let t29;
  if ($[107] !== t10 || $[108] !== t28 || $[109] !== t9) {
    t29 = <div className={t9}>{t10}{t28}</div>;
    $[107] = t10;
    $[108] = t28;
    $[109] = t9;
    $[110] = t29;
  } else {
    t29 = $[110];
  }
  let t30;
  if ($[111] !== t11 || $[112] !== t12 || $[113] !== t29) {
    t30 = <div onClick={t11} className={t12}>{t29}</div>;
    $[111] = t11;
    $[112] = t12;
    $[113] = t29;
    $[114] = t30;
  } else {
    t30 = $[114];
  }
  let t31;
  if ($[115] !== T0 || $[116] !== t13 || $[117] !== t14 || $[118] !== t15 || $[119] !== t16 || $[120] !== t30) {
    t31 = <T0 variants={t13} initial={t14} animate={t15} custom={t16}>{t30}</T0>;
    $[115] = T0;
    $[116] = t13;
    $[117] = t14;
    $[118] = t15;
    $[119] = t16;
    $[120] = t30;
    $[121] = t31;
  } else {
    t31 = $[121];
  }
  return t31;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp4(e_2) {
  return e_2.stopPropagation();
}
function _temp5(e_3) {
  return e_3.stopPropagation();
}